import { useState } from "react";
import { MailCheck, X, RefreshCw } from "lucide-react";
import { authService } from "@/api/services/authService";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

export default function EmailVerificationBanner() {
  const { user, reload } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  // Only show for logged-in users who haven't verified their email
  if (!user || user.emailVerified || user.status === "ACTIVE" || dismissed) return null;

  const handleResend = async () => {
    setResending(true);
    try {
      await authService.resendActivation(user.email);
      toast.success("A new 6-digit code has been sent to your inbox.");
      setCode("");
    } catch {
      toast.error("Failed to resend. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setVerifying(true);
    try {
      await authService.activate(code);
      toast.success("Email verified! Your account is now active.");
      await reload();
    } catch {
      toast.error("Invalid or expired code. Request a new one.");
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-3">
        <MailCheck className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <p className="text-sm text-amber-800 dark:text-amber-300">
          Action needed: Verify your email to write reviews, claim businesses, and make bookings.{" "}
          <span className="font-medium">A 6-digit code was sent to {user.email}.</span>
        </p>
        <div className="flex items-center gap-2 ml-auto">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="Enter code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="w-28 text-center text-sm border border-amber-300 dark:border-amber-700 rounded-md px-2 py-1 bg-white dark:bg-amber-950 text-amber-900 dark:text-amber-100 placeholder:text-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
          <button
            onClick={handleVerify}
            disabled={code.length !== 6 || verifying}
            className="text-xs font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-md px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {verifying ? "Verifying…" : "Verify"}
          </button>
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-xs font-medium text-amber-700 dark:text-amber-300 underline underline-offset-2 hover:no-underline disabled:opacity-50 flex items-center gap-1 flex-shrink-0"
          >
            {resending && <RefreshCw className="h-3 w-3 animate-spin" />}
            Resend
          </button>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 flex-shrink-0"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
